import React, { useState } from "react";
import { Button, EmptyState, Menu, Plus, DotsVertical, Edit01, Trash01, Table, CellText, LayersThree01 } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import InventoryPoolModal from "./InventoryPoolModal";
import ConfirmDialog from "./ConfirmDialog";
import { toastError } from "../../../shared/toastHelper";

const InventoryPoolsCard = () => {
  const { series, handleDeleteInventoryPool } = useSeriesShowContext();
  const pools = series.series_pass_inventory_pools || [];
  const products = series.series_pass_products || [];
  const [modalOpen, setModalOpen] = useState(false);
  const [editPool, setEditPool] = useState(null);
  const [deletePool, setDeletePool] = useState(null);

  const openCreate = () => {
    setEditPool(null);
    setModalOpen(true);
  };

  const openEdit = (pool) => {
    setEditPool(pool);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditPool(null);
  };

  const confirmDelete = async (pool) => {
    try {
      await handleDeleteInventoryPool(pool.id);
    } catch (e) {
      const apiErrors = e && e.response && e.response.data && e.response.data.errors;
      toastError(
        Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors.join(", ")
          : "Could not delete this pool. Please try again.",
      );
    }
  };

  const memberNames = (pool) =>
    products
      .filter((product) => product.series_pass_inventory_pool_id === pool.id)
      .map((product) => product.name);

  const deleteBody = (pool) => {
    if (!pool) return "";
    const members = memberNames(pool);
    if (pool.in_use) {
      return `"${pool.name}" still holds ${members.join(", ")}. A pool can only be deleted once it is empty, and a product can only leave a pool before it has sold anything.`;
    }
    return `Delete "${pool.name}"? This can't be undone.`;
  };

  const columns = [
    { key: "name", header: "Name", primary: true, render: (pool) => <CellText primary={pool.name} /> },
    {
      key: "products",
      header: "Products",
      render: (pool) => (
        <span data-testid={`pool-members-${pool.id}`}>
          {memberNames(pool).join(", ") || <span className="text-muted">None yet</span>}
        </span>
      ),
    },
    { key: "capacity", header: "Capacity", align: "right", render: (pool) => pool.capacity },
    { key: "sold", header: "Sold", align: "right", render: (pool) => pool.sold_count },
    {
      key: "remaining",
      header: "Remaining",
      align: "right",
      render: (pool) => (
        <span data-testid={`pool-remaining-${pool.id}`}>
          {pool.remaining === 0 ? (
            <span className="series-status-pill series-status-past">Sold out</span>
          ) : (
            pool.remaining
          )}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (pool) => (
        <Menu
          placement="bottom-end"
          items={[
            { label: "Edit pool", icon: Edit01, onClick: () => openEdit(pool) },
            { label: "Delete pool", icon: Trash01, destructive: true, onClick: () => setDeletePool(pool) },
          ]}
          trigger={(
            <Button
              variant="tertiary"
              size="sm"
              icon={DotsVertical}
              aria-label={`Actions for ${pool.name}`}
              data-testid={`pool-actions-btn-${pool.id}`}
            />
          )}
        />
      ),
    },
  ];

  return (
    <div className="series-schedule-card mt-4" data-testid="inventory-pools-card">
      <div className="series-schedule-header d-flex align-items-center justify-content-between">
        <h4 className="series-schedule-title mb-0">Shared Inventory</h4>
        {series.state !== "canceled" && (
          <Button
            variant="secondary"
            icon={Plus}
            data-testid="create-inventory-pool-btn"
            onClick={openCreate}
          >
            Add pool
          </Button>
        )}
      </div>

      <div data-testid={pools.length === 0 ? "inventory-pools-empty" : "inventory-pools-table"}>
        <Table
          columns={columns}
          data={pools}
          getRowKey={(pool) => pool.id}
          empty={(
            <EmptyState
              size="sm"
              icon={LayersThree01}
              title="No shared inventory"
              description="Let several passes and packs sell from one combined allotment. Add a pool, then choose it when you create or edit a product."
            />
          )}
        />
      </div>

      <InventoryPoolModal open={modalOpen} pool={editPool} onClose={closeModal} />
      <ConfirmDialog
        open={deletePool !== null}
        title="Delete this pool?"
        body={deleteBody(deletePool)}
        confirmLabel="Delete pool"
        confirmDisabled={deletePool !== null && deletePool.in_use}
        onConfirm={() => confirmDelete(deletePool)}
        onClose={() => setDeletePool(null)}
      />
    </div>
  );
};

export default InventoryPoolsCard;
